import { capitalize, isEmpty } from '@/lib/collections';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Info, Search,
    Wrench, AlertTriangle, UserPlus
} from 'lucide-react';
import useIsPWA from '@/hooks/use-is-pwa';
import { PictoEconomie, PictoRapidite } from '@/assets/icons-april-fr/pictos';
import DetailSinistreDocuments from '@/pages/accident/sinistre/detail-sinistre/detail-sinistre-documents';
import { formatEuro } from '@/lib/helpers';
import Timeline from '@/components/shared/timeline';
import { useSelectedContract } from '@/hooks/api/use-contracts';
import { isWakamContract } from '@/lib/contracts';
import { formatDate } from '@/lib/dates';
import type { Accident } from '@/types/api/accident';

type BadgeVariant = 'warning' | 'destructive' | 'secondary' | 'default';

interface DetailSinistreProps {
    accident: Accident;
}

const DetailSinistre = ({ accident }: DetailSinistreProps) => {
    const isWakam = isWakamContract(useSelectedContract());
    const isPWA = useIsPWA();
    const warning = accident.situationCode === '1' && isEmpty(accident.reparateur) && accident.categorieCode !== '10';

    const hasCoordonneeGarage = accident.documents?.find((d) => d.code === '23');
    const hasCoordonneeGarageNotReceived = hasCoordonneeGarage?.recu === false;

    const getStatusVariant = (situationCode?: string): BadgeVariant => {
        switch (situationCode) {
            case '1': return 'warning';
            case '2':
            case '8': return 'destructive';
            case 'A': return 'warning';
            case '3': return 'secondary';
            default: return 'default';
        }
    };

    const isClosed = ['2', '8'].includes(accident.situationCode as string);
    const heroGradient = isClosed
        ? 'bg-muted-foreground'
        : 'bg-destructive';

    return (
        <div className="space-y-6">
            {/* Hero Section */}
            <div className={`relative overflow-hidden rounded-2xl ${heroGradient} p-6 text-white`}>
                <div className="absolute -top-1/2 -right-[10%] w-[400px] h-[400px] bg-white/10 rounded-full" />
                <div className="relative z-10">
                    <p className="text-xs sm:text-sm opacity-90 mb-1">Sinistre N°{accident.numCbt}</p>
                    <h1 className="text-xl sm:text-3xl font-semibold mb-6">{accident.libelle}</h1>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:[grid-template-columns:repeat(auto-fit,minmax(180px,1fr))] gap-3 sm:gap-6">
                        <div>
                            <p className="text-xs sm:text-sm opacity-80 mb-0.5">Survenu le</p>
                            <p className="text-sm sm:text-lg font-semibold">{formatDate(accident.dateSurv)}</p>
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm opacity-80 mb-0.5">Lieu</p>
                            <p className="text-sm sm:text-lg font-semibold">{accident.villeAccident} ({accident.codePostalAccident})</p>
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm opacity-80 mb-0.5">Type</p>
                            <p className="text-sm sm:text-lg font-semibold">{accident.categorie}</p>
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm opacity-80 mb-0.5">Statut</p>
                            <Badge variant={getStatusVariant(accident.situationCode)} className={isPWA ? "text-sm px-6 py-1.5" : "text-xs sm:text-sm px-2.5 sm:px-4 py-1"}>
                                {capitalize(accident.situation)}
                            </Badge>
                        </div>
                    </div>
                </div>
            </div>

            {/* Timeline */}
            {accident.documents && !isWakam && accident.situationCode !== '8' && <Timeline accident={accident} />}

            {/* Alert situationCode "A" */}
            {accident.situationCode === 'A' && (
                <Alert variant="warning" className="items-center has-[>svg]:grid-cols-[calc(var(--spacing)*6)_1fr] [&>svg]:size-6 [&>svg]:translate-y-0">
                    <PictoRapidite className="h-6 w-6" />
                    <AlertDescription>
                        Votre déclaration de sinistre du {formatDate(accident.dateOuverture)} a bien été prise en compte et est en attente d'ouverture par l'un de nos conseillers. Nous reviendrons rapidement vers vous après étude de votre dossier.
                    </AlertDescription>
                </Alert>
            )}

            {/* Alert situationCode "3" */}
            {accident.situationCode === '3' && (
                <Alert variant="warning">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        {accident.documents?.find((d) => d.code === 'C') && accident.documents.find((d) => d.code === 'C')?.recu
                            ? `Votre déclaration du sinistre du ${formatDate(accident.dateOuverture)} a bien été prise en compte et est en attente d'ouverture par l'un de nos conseillers. Nous reviendrons rapidement vers vous après étude de votre dossier.`
                            : "La responsabilité n'est pas encore déterminée car le justificatif de la déclaration est en attente."
                        }
                    </AlertDescription>
                </Alert>
            )}

            {/* Alert situationCode "8" Corporel */}
            {(accident.situationCode === '8' && accident.type === 'Corporel') && (
                <Alert variant="warning">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        La partie matérielle de votre véhicule est clôturée, nous vous invitons à vous rapprocher d'Allianz pour toutes informations sur la partie corporelle : corporel@allianz.fr
                    </AlertDescription>
                </Alert>
            )}

            {/* Documents (haut) - quand il y a des docs non reçus */}
            {accident.documents?.find((d) => d.recu === false) && (
                <DetailSinistreDocuments contractId={accident.id_contrat} accidentId={accident.id} documents={accident.documents} situationCode={accident.situationCode} upload_app_link={accident.upload_app_link} />
            )}

            {/* Alert no réparateur */}
            {(!accident.reparateur?.raisonSociale && warning) && hasCoordonneeGarage && hasCoordonneeGarageNotReceived && (
                /* Le bouton rend la ligne plus haute que le texte : on centre le picto avec elle */
                <Alert variant="destructive" className="sm:items-center sm:[&>svg]:translate-y-0">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <p className="text-sm font-medium">Attention ! Aucun réparateur n'a été missionné pour votre sinistre.</p>
                            <Button asChild size="sm" variant="destructive" className="cursor-pointer shrink-0">
                                <Link to={`/mes-sinistres/contrat/${accident.id_contrat}/sinistre/${accident.id}/ajouter-reparateur`}>
                                    <UserPlus className="w-4 h-4" />
                                    Ajouter un réparateur
                                </Link>
                            </Button>
                        </div>
                    </AlertDescription>
                </Alert>
            )}

            {/* Informations */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Info className="w-5 h-5 text-primary" />
                        Informations
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Lieu de l'accident</p>
                            <p className="font-medium">{accident.villeAccident} ({accident.codePostalAccident})</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Véhicule</p>
                            <p className="font-medium">{accident.libelle}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Survenu le</p>
                            <p className="font-medium">{formatDate(accident.dateSurv)}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Circonstances</p>
                            <p className="font-medium">{accident.circonstances}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Dernière intervention</p>
                            <p className="font-medium">{accident.dateDerniereIntervention ? formatDate(accident.dateDerniereIntervention) : '—'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Statut</p>
                            <Badge variant={getStatusVariant(accident.situationCode)}>
                                {capitalize(accident.situation)}
                            </Badge>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Taux de responsabilité</p>
                            <p className="font-medium">
                                {accident.situationCode === '3'
                                    ? 'Non déterminée'
                                    : accident.situationCode === 'A'
                                        ? 'En attente'
                                        : `${accident.tauxResp}%`
                                }
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Type de sinistre</p>
                            <p className="font-medium">{accident.categorie}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Expert */}
            {accident.situationCode !== 'A' && (
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Search className="w-5 h-5 text-primary" />
                            Expert
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        {accident.expert?.raisonSociale ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">Expert missionné</p>
                                    <p className="font-medium">{accident.expert.raisonSociale}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">Numéro de référence</p>
                                    <p className="font-medium">{accident.expert.reference}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">Passage programmé pour</p>
                                    <p className="font-medium">{formatDate(accident.datePassageExpert)}</p>
                                </div>
                                {accident.expert.id !== '365585' && (
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-0.5">Contactez l'expert</p>
                                        <p className="font-medium">{accident.expert.phone} ou par mail à {accident.expert.email}</p>
                                    </div>
                                )}
                                {accident.expert.id !== '365585' && (
                                    <div className="sm:col-span-2">
                                        <p className="text-sm text-muted-foreground mb-0.5">Adresse postale</p>
                                        <p className="font-medium">{accident.expert.rue1} {accident.expert.rue2 !== '' ? `- ${accident.expert.rue2}` : ''} - {accident.expert.codePostal} {accident.expert.ville}</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">Aucun expert n'a été missionné pour votre sinistre.</p>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Réparateur (avec données) */}
            {accident.reparateur?.raisonSociale && (
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Wrench className="w-5 h-5 text-primary" />
                            Réparateur
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
                            <div>
                                <p className="text-sm text-muted-foreground mb-0.5">Réparateur agréé</p>
                                <p className="font-medium">{accident.reparateur.raisonSociale}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-0.5">Contactez le réparateur</p>
                                <p className="font-medium">{accident.reparateur.phone} ou par mail à {accident.reparateur.email}</p>
                            </div>
                            <div className="sm:col-span-2">
                                <p className="text-sm text-muted-foreground mb-0.5">Adresse postale</p>
                                <p className="font-medium">{accident.reparateur.rue1} {accident.reparateur.rue2 !== '' ? `- ${accident.reparateur.rue2}` : ''} - {accident.reparateur.codePostal} {accident.reparateur.ville}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Réparateur (vide, pas de warning) */}
            {(!accident.reparateur?.raisonSociale && !warning) && (
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Wrench className="w-5 h-5 text-primary" />
                            Réparateur
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <p className="text-sm text-muted-foreground">Aucun réparateur n'a été missionné pour votre sinistre.</p>
                    </CardContent>
                </Card>
            )}

            {/* Règlement effectué */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <PictoEconomie className="w-5 h-5" />
                        Règlement effectué
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                    {accident.reglements.length > 0 ? (
                        <div className="grid gap-3">
                            {accident.reglements.map((reglement, index) =>
                                reglement.debit > 0 ? (
                                    <div key={index} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                                        <p className="font-medium">
                                            Règlement émis auprès de {reglement.raisonSociale} le {formatDate(reglement.date)}
                                        </p>
                                        <p className="text-sm text-muted-foreground shrink-0 ml-4">
                                            {formatEuro(reglement.debit)}
                                        </p>
                                    </div>
                                ) : null
                            )}
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground">Aucun règlement n'a encore été effectué.</p>
                    )}
                </CardContent>
            </Card>

            {/* Documents (bas) - quand tous les docs sont reçus */}
            {!accident.documents?.find((d) => d.recu === false) && (
                <DetailSinistreDocuments contractId={accident.id_contrat} accidentId={accident.id} documents={accident.documents} situationCode={accident.situationCode} upload_app_link={accident.upload_app_link ?? null} />
            )}
        </div>
    );
};

export default DetailSinistre;
